import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Upload, History, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useReviewFlow } from "@/components/schema/ReviewFlow";
import HistoryDialog from "@/components/schema/HistoryDialog";
import { downloadJson } from "@/lib/schema";

// Export / Import / (optional) Reset and History buttons for one stored record.
//   exportUrl: GET returning the envelope           importCall: ({dryRun, allowOlder}, json) => axios promise
//   resetCall: ({dryRun}) => promise                 history: {kind, recordId, restorable}
export default function ImportExport({ exportUrl, filename, importCall, resetCall, history, onChanged, canWrite = true, hideExport = false, showTransfer = true }) {
  const fileRef = useRef(null);
  const [histOpen, setHistOpen] = useState(false);
  const flow = useReviewFlow(onChanged);

  const doExport = async () => {
    try {
      const { data } = await api.get(exportUrl);
      downloadJson(data, filename);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    let json;
    try { json = JSON.parse(await f.text()); } catch (err) { toast.error(`Not valid JSON: ${err.message}`); return; }
    flow.start(`Import ${f.name}`, (opts) => importCall(opts, json));
  };

  return (
    <div className="flex flex-wrap gap-2">
      {showTransfer && !hideExport && <Button variant="outline" size="sm" onClick={doExport} data-testid="export-json"><Download className="h-4 w-4 mr-1" /> Export JSON</Button>}
      {showTransfer && canWrite && importCall && (
        <>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="import-json">
            <Upload className="h-4 w-4 mr-1" /> Import JSON
          </Button>
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onFile} />
        </>
      )}
      {showTransfer && canWrite && resetCall && (
        <Button variant="outline" size="sm" onClick={() => flow.start("Reset to seed", resetCall)} data-testid="reset-seed">
          <RotateCcw className="h-4 w-4 mr-1" /> Reset to seed
        </Button>
      )}
      {history && (
        <Button variant="outline" size="sm" onClick={() => setHistOpen(true)} data-testid="open-history">
          <History className="h-4 w-4 mr-1" /> History
        </Button>
      )}
      {flow.dialog}
      {history && (
        <HistoryDialog open={histOpen} onClose={() => setHistOpen(false)} {...history} canRestore={canWrite} onRestored={onChanged} />
      )}
    </div>
  );
}
